<template>
  <div class="card pb-4">
    <h2 class="subtitle mb-3">Event Information</h2>
    <div class="row">
      <div class="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12">
        <base-input
          v-model="v.name.$model"
          label="Name"
          placeholder="Annual Sales Summit"
          :maxlength="EVENT_NAME_MAX_LENGTH"
          markrequired
          :error="v.name.$error ? 'Enter a Name' : ''"
        />
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <base-input
          v-model="v.code.$model"
          label="Code"
          placeholder="SUMMIT26"
          :maxlength="EVENT_CODE_MAX_LENGTH"
          markrequired
          :error="codeError"
        />
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <label class="title-label">Status</label>
        <b-form-group class="mb-3">
          <b-form-radio-group
            v-model="value.status"
            :options="statusOptions"
            name="event_status"
            buttons
            button-variant="outline-neutral"
            size="md"
            class="pc-0"
          />
        </b-form-group>
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <base-input
          label="Start Date"
          markrequired
          :error="v.startDate.$error ? 'Select a Start Date' : ''"
        >
          <flat-picker
            slot-scope="{ focus, blur }"
            @on-open="focus"
            @on-close="blur"
            :config="{
              altFormat: 'd-M-Y',
              altInput: true,
              dateFormat: 'Y-m-d',
              allowInput: true
            }"
            class="input-custom"
            v-model="v.startDate.$model"
            placeholder="Start date"
          ></flat-picker>
        </base-input>
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <base-input label="End Date" markrequired :error="endDateError">
          <flat-picker
            slot-scope="{ focus, blur }"
            @on-open="focus"
            @on-close="blur"
            :config="{
              altFormat: 'd-M-Y',
              altInput: true,
              dateFormat: 'Y-m-d',
              allowInput: true,
              minDate: value.startDate || null
            }"
            class="input-custom"
            v-model="v.endDate.$model"
            placeholder="End date"
          ></flat-picker>
        </base-input>
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <WSelect
          v-model="v.timezone.$model"
          label="Time Zone"
          placeholder="Select a Time Zone"
          filterable
          markrequired
          :error="v.timezone.$error ? 'Select a Time Zone' : ''"
        >
          <el-option
            v-for="timezone in timezones"
            :key="timezone"
            :label="timezone"
            :value="timezone"
          />
        </WSelect>
      </div>

      <div class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
        <WSelect
          v-model="value.customer"
          label="Customer"
          placeholder="Type 3 letters to search"
          filterable
          remote
          :remote-method="searchCustomers"
          clearable
        >
          <el-option
            v-for="customer in customerResults"
            :key="customer._id"
            :label="getCustomerOptionLabel(customer)"
            :value="customer._id"
          >
            <div class="row">
              <div class="col-7">
                <span class="role-table" v-if="isCompanyCustomer(customer.type)">{{
                  customer.company_name
                }}</span>
                <span>{{ customer.first_name }} {{ customer.last_name }}</span>
              </div>
              <div class="col-5 text-right hide-data">
                <span class="coin-right">{{
                  formatClientType(customer.type)
                }}</span>
              </div>
            </div>
          </el-option>
        </WSelect>
      </div>

      <div class="col-12">
        <label class="title-label">Description</label>
        <textarea
          class="input-custom form-control"
          v-model="v.description.$model"
          :maxlength="EVENT_DESCRIPTION_MAX_LENGTH"
          rows="3"
          placeholder="Details of the event"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import WSelect from "@/components/Select";
import {
  EVENT_NAME_MAX_LENGTH,
  EVENT_CODE_MAX_LENGTH,
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_STATUS_FILTERS
} from "../constants/events.constants";
import {
  getCustomerOptionLabel,
  isCompanyCustomer
} from "../services/event-format.service";
import { formatClientType } from "@/modules/reservations/services/format.service";

const TIMEZONES = moment.tz.names();

export default {
  name: "EventForm",
  props: {
    // Event form, edited in place (see `buildEmptyEventForm`).
    value: {
      type: Object,
      required: true
    },
    // Vuelidate state of the form (`$v.event` of the page).
    v: {
      type: Object,
      required: true
    },
    customerOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      EVENT_NAME_MAX_LENGTH,
      EVENT_CODE_MAX_LENGTH,
      EVENT_DESCRIPTION_MAX_LENGTH,
      timezones: TIMEZONES,
      customerSearch: "",
      statusOptions: EVENT_STATUS_FILTERS.map(status => ({
        text: status.label,
        value: status.value
      }))
    };
  },
  computed: {
    // Customers matching the search, plus the selected one so its name shows.
    customerResults() {
      const selected = this.customerOptions.filter(
        customer => customer._id === this.value.customer
      );
      if (this.customerSearch.length < 3) return selected;
      const search = this.customerSearch.toLowerCase();
      const matches = this.customerOptions.filter(
        customer =>
          customer._id !== this.value.customer &&
          getCustomerOptionLabel(customer)
            .toLowerCase()
            .includes(search)
      );
      return [...selected, ...matches];
    },
    codeError() {
      if (!this.v.code.$error) return "";
      return this.v.code.validCode
        ? "Enter a Code"
        : "Use only letters, numbers, - and _";
    },
    endDateError() {
      if (!this.v.endDate.$error) return "";
      return this.v.endDate.afterStartDate
        ? "Select an End Date"
        : "The End Date must be on or after the Start Date";
    }
  },
  methods: {
    getCustomerOptionLabel,
    isCompanyCustomer,
    formatClientType,
    searchCustomers(input = "") {
      this.customerSearch = input.trim();
    }
  },
  components: {
    flatPicker,
    WSelect
  }
};
</script>
